/**
 * The Inbox — threads and messages (`chorus-byggeplan.md` Fase 1.3;
 * `docs/design/00-contracts.md` §6.17–§6.19, §12.5, §12.6).
 *
 * > Trådbasert intern meldingsfunksjon (ikke e-postklient). Tråder kan stå fritt eller kobles til
 * > et objekt (kontakt, festival; senere performance). Ulest-markering, notifikasjoner ved nye
 * > meldinger. Struktur bygges slik at en tråd senere kan få e-postmeldinger inn (felt for kilde:
 * > internal/email), men e-postintegrasjon bygges IKKE nå.
 *
 * ---------------------------------------------------------------------------------------------
 * UNREAD IS A BOOLEAN, AND THAT IS A DECISION ABOUT WHAT "UNREAD" MEANS
 *
 * There are two things "unread" can mean, and they cost different amounts:
 *
 * * **"How many messages have I not read?"** — a number. Answering it exactly needs a read
 *   receipt per message per participant (20 000 messages × 5 participants is 100 000 rows to
 *   maintain so a badge can say "3"), or a `COUNT(*)` over `messages` per thread in the page,
 *   which is an N+1 across forty inbox rows and a sequential relationship between the badge and
 *   the length of the conversation.
 * * **"Is there anything here I have not seen?"** — a boolean, and the one an inbox actually
 *   acts on: the reader opens the thread and reads what is new. "3 new" and "new" lead to the
 *   same click.
 *
 * Tvitvi answers the second. `hasUnread` is `thread_participants.last_message_at >
 * thread_participants.last_read_at` — two columns of the row the list is already reading, so it
 * costs nothing, has no join and cannot become an N+1. Read state belongs to the reader: it is
 * on the participant row, never on the thread, so two people looking at the same thread see
 * different answers, which is the whole point.
 *
 * `?unread=true` narrows the list to exactly those rows, off a partial index whose predicate is
 * that same comparison (migration 0010).
 *
 * ---------------------------------------------------------------------------------------------
 * THE SORT IS VOLATILE, SO THE DIRECTION IS FIXED
 *
 * An inbox is ordered by last activity, and last activity changes every time anybody posts.
 * A keyset over a column that moves is only safe if it moves in ONE direction, and this one
 * does: `last_message_at` is only ever set forward, by `app.bump_thread_activity()`.
 *
 * Descending, that monotonicity is a guarantee. A thread that receives a message while the
 * reader is on page three moves *towards the head* — past the cursor in the direction the reader
 * has already been — so it is never emitted twice. At worst it is missed by this pagination run
 * and is sitting at the top of page one, which is where the reader is looking next anyway.
 *
 * Ascending, the same movement carries the row *forward across* the cursor, and it comes back a
 * second time on a later page. A duplicate breaks a keyed list in a client; a row that jumps to
 * the top does not. So `GET /v1/threads` is descending, and says so in the contract instead of
 * leaving an `?direction=asc` trap for the first person who tries it.
 *
 * Messages have no such problem — `sent_at` is written once and never moves — so
 * `GET /v1/threads/:id/messages` pages both ways off one index.
 *
 * ---------------------------------------------------------------------------------------------
 * THE EMAIL FIELD EXISTS AND DOES NOTHING
 *
 * `messages.source` is `internal | email`, and `MessageDto` reports it, because a client that
 * will one day render an email differently from an internal message needs to be able to tell
 * them apart from the first release. That is the whole of it:
 *
 * * `CreateMessageRequest` has no `source`. Every message this API writes is `internal`, set by
 *   the service, so nothing on the wire can claim to have arrived by email.
 * * There is no ingestion, no parsing, no outbound, no address on a thread, and no
 *   `externalMessageId` in any DTO. `ux_messages__external_id` and `messages.external_message_id`
 *   are waiting in the schema for Phase 3 and are not part of any contract here.
 * * `bodyFormat` is likewise read-only. Everything this API writes is `markdown`; `html` exists
 *   for the inbound email that does not exist yet, and accepting it from a client would be an
 *   injection vector bought for a feature nobody has asked for.
 */
import { z } from 'zod';

import { messageFormatSchema, messageSourceSchema, objectTypeSchema } from '../enums.js';
import { cursorQuerySchema, page, sortDirectionSchema } from '../http/pagination.js';
import { attachmentDtoSchema } from './attachments.js';
import { booleanQuerySchema } from './common.js';

/**
 * What a thread may be attached to. The plan says "kontakt, festival; senere performance", and
 * `edition` is in the list because a festival's operational year is the edition — it is what the
 * seed links to and what `threads.edition_id` is denormalized for. `performance` is Phase 2 and
 * is deliberately absent: `object_type` has the value, this contract does not.
 */
export const THREAD_LINK_TYPES = ['contact', 'festival', 'edition'] as const;
export type ThreadLinkType = (typeof THREAD_LINK_TYPES)[number];
export const threadLinkTypeSchema = z.enum(THREAD_LINK_TYPES).meta({ id: 'ThreadLinkType' });

/**
 * The one sort `GET /v1/threads` offers, and the only one an inbox has: last activity.
 *
 * Backed by `ix_thread_participants__inbox (organization_id, person_id, last_message_at DESC,
 * thread_id DESC) WHERE deleted_at IS NULL` — the participant's own copy of the thread's
 * activity, which is what makes "my threads, newest first, with unread state" a single index
 * range scan instead of a sort over a join.
 *
 * There is deliberately no `subject` sort: no index orders it, and an inbox sorted by subject is
 * not an inbox. Adding a sort starts with a migration, not with a string in this array.
 */
export const THREAD_SORT_KEYS = ['activity'] as const;
export type ThreadSortKey = (typeof THREAD_SORT_KEYS)[number];

/**
 * The one sort a thread's messages have. Backed by `ix_messages__org_thread_sent
 * (organization_id, thread_id, sent_at DESC, id DESC) WHERE deleted_at IS NULL`, read forwards
 * for `desc` and backwards for `asc`.
 */
export const MESSAGE_SORT_KEYS = ['sent'] as const;
export type MessageSortKey = (typeof MESSAGE_SORT_KEYS)[number];

/** Long enough for a real message, short enough not to be a document. Markdown. */
const bodySchema = z.string().trim().min(1).max(20_000);
const subjectSchema = z.string().trim().min(1).max(200);

/** How much of the last message the inbox row carries. Truncated in SQL, never in the client. */
export const MESSAGE_PREVIEW_LENGTH = 240;

/** How many participants an inbox row names before it stops and lets `participantCount` speak. */
export const THREAD_PARTICIPANT_PREVIEW = 8;

/** The most people one request may add to a thread. */
export const MAX_THREAD_PARTICIPANTS = 50;

/**
 * A person in a thread, as every list that shows one needs them: the person, and the contact row
 * this organization knows them through.
 *
 * `contactId` and `displayName` are null together in one state, the same one as on the Tasks
 * page: a contact soft-deleted after they were added. The participation keeps pointing at the
 * person — that is what `person_id` is for — and the client renders an unknown-person
 * placeholder rather than losing the row.
 */
export const threadParticipantDtoSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    displayName: z.string().nullable(),
    /**
     * Huset denne personen sitter i (QA14 #7).
     *
     * Atle ba om «organisasjon per person» i deltakerpopoveren. Valgfritt fordi tre andre ruter
     * bruker den samme formen — programpostens chat og forhaandsvisningen i lista — og de trenger
     * den ikke.
     */
    organizationName: z.string().nullable().optional(),
    /**
     * Og husets id, så lista kan gruppere per hus uten å stole på at to hus heter forskjellig
     * (QA-runde 3 punkt 11). Valgfri av samme grunn som navnet.
     */
    organizationId: z.uuid().nullable().optional(),
    /**
     * HVOR LANGT DENNE PERSONEN HAR LEST (KOI Del 4: «sett av …» under siste melding).
     *
     * `null` betyr «har aldri åpnet tråden», og det er en ekte tilstand og ikke en manglende
     * verdi: kolonnen er NOT NULL med `-infinity` i basen, og DTO-en oversetter sentinelen —
     * en klient skal ikke måtte kjenne en dato i 4713 f.Kr.
     *
     * VALGFRITT I SKJEMAET, fordi tre andre ruter bruker den samme formen uten å svare på
     * spørsmålet: programpostens chat har sin egen `readers`-liste, og forhåndsvisningen i
     * innbokslista trenger den ikke. En påkrevd kolonne der ville vært en spørring til for
     * hver av dem, for noe ingen av dem tegner.
     */
    lastReadAt: z.iso.datetime().nullable().optional(),
  })
  .meta({ id: 'ThreadParticipant' });

/**
 * HVORFOR DU FØLGER EN TRÅD (0931, grep E1). Tilgangen kommer fra det tråden gjelder; følgingen er
 * det som gir Innboks, uleste og varsler, og den har alltid en grunn — som GitHub sier hvorfor du får
 * et varsel. Ordforrådet er basens (`ck_thread_followers__reason`).
 */
export const THREAD_FOLLOW_REASONS = [
  'started',
  'replied',
  'mentioned',
  'responsible',
  /* 260.14: der ingen er satt som ansvarlig — «ingen ansvarlig ennå». */
  'no_responsible',
  'added',
  'recipient',
  'manual',
] as const;
export type ThreadFollowReason = (typeof THREAD_FOLLOW_REASONS)[number];
export const threadFollowReasonSchema = z
  .enum(THREAD_FOLLOW_REASONS)
  .meta({ id: 'ThreadFollowReason' });

/** En som følger tråden, med grunnen og huset hen følger fra. */
export const threadFollowerDtoSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    displayName: z.string().nullable(),
    organizationId: z.uuid(),
    organizationName: z.string().nullable(),
    reason: threadFollowReasonSchema,
  })
  .meta({ id: 'ThreadFollower' });
export type ThreadFollowerDto = z.infer<typeof threadFollowerDtoSchema>;

/** «Følg» / «Slutt å følge» — for leseren selv, i huset hen opptrer som. */
export const followThreadRequestSchema = z
  .object({ following: z.boolean() })
  .meta({ id: 'FollowThreadRequest' });
export type FollowThreadRequest = z.infer<typeof followThreadRequestSchema>;

/** What the thread is about, when it is about something. Both halves or neither (§6.17). */
export const threadLinkDtoSchema = z
  .object({
    type: objectTypeSchema,
    id: z.uuid(),
  })
  .meta({ id: 'ThreadLink' });

/**
 * The last message, as the inbox row shows it. `preview` is `left(body, 240)` from the database:
 * a fifty-row page must not carry fifty full messages, and truncating in the client would mean
 * having sent them first.
 */
export const messagePreviewDtoSchema = z
  .object({
    id: z.uuid(),
    author: threadParticipantDtoSchema.nullable(),
    preview: z.string(),
    sentAt: z.iso.datetime(),
    source: messageSourceSchema,
    /**
     * `system` er en linje uten forfatter med JSON-kropp (0462) — raden oversetter den i stedet for
     * å vise JSON. Da er `preview` hele kroppen, ikke kappet.
     */
    bodyFormat: messageFormatSchema.optional(),
  })
  .meta({ id: 'MessagePreview' });

/**
 * One row of the inbox. Every field here is answered by the page's own query — the participant
 * row, the thread, and two laterals bounded by the page — so a list of forty threads is one
 * round trip (principle 6: no N+1).
 */
export const threadSummaryDtoSchema = z
  .object({
    id: z.uuid(),
    subject: z.string(),
    /**
     * Lukket FOR ALLE: ingen kan skrive i den (§3.3e). Ikke arkivet — se `isArchived`.
     */
    isClosed: z.boolean(),
    /**
     * LESERENS ARKIV (sak 215.8, 0391). Per leser, som i e-post: å arkivere lukker ikke tråden for
     * motparten, og en ny melding fra en annen legger den tilbake i innboksen. Valgfri fordi
     * chatrutene bruker den samme formen uten å ha en innboks.
     */
    isArchived: z.boolean().optional(),
    /**
     * DEMPET AV LESEREN (F48, 0700). Nye meldinger gir ikke varsel; en @-nevning gjør det fortsatt.
     * Valgfri av samme grunn som `isArchived`.
     */
    isMuted: z.boolean().optional(),
    /** Null for a free-standing thread, which is a first-class state, not a missing value. */
    link: threadLinkDtoSchema.nullable(),
    /**
     * GJESTENS EGET ANKER I EN DELT TRÅD (QA14 #8).
     *
     * `link` er EIERENS: i en delt budtråd peker den på festivalens `booking_bid`. Byrået kan
     * ikke åpne den raden — den ligger i et hus de ikke er medlem av — så en knapp bygget på
     * `link` ville sendt dem til en 404.
     *
     * Tråden har båret begge sider siden 0232 (`counterparty_linked_object_*`), men kolonnene ble
     * aldri projisert ut av API-et. Dette er de, og de er allerede riktige per leser: den som er
     * gjest ser sitt eget objekt, den som eier tråden ser null her og bruker `link`.
     */
    counterpartyLink: threadLinkDtoSchema.nullable(),
    /**
     * DET ANDRE HUSETS NAVN (QA13 runde 2, J7/J8).
     *
     * Atle: «"den andre parten" skal være organisasjonens navn.» Merkelappen på en boble sa
     * hvilken SIDE avsenderen står på og ikke hvem det er — og i en delt samtale er det nettopp
     * navnet man trenger, fordi den samme personen kan være medlem begge steder (QA M6).
     *
     * PÅ TRÅDEN OG IKKE PÅ HVER MELDING. Verdien er én per samtale, og et oppslag per boble ville
     * vært tjue like oppslag på én skjerm (prinsipp 6). Boblen vet allerede hvilken side den står
     * på gjennom `authorIsCounterparty`; det eneste som manglet var navnet å sette på den siden.
     *
     * KILDEN ER `organization_links.counterparty_name` OG IKKE `organizations.name`. Det andre
     * husets egen rad er ikke lesbar for oss — RLS slipper den ikke gjennom, og det er meningen.
     * Koblingsraden er VÅR, den bærer navnet vi ble presentert for, og den er det eneste stedet
     * dette navnet lovlig finnes på vår side.
     *
     * Null for en tråd som ikke er delt, som er den vanligste: da har den ingen annen part.
     */
    counterpartyName: z.string().nullable(),
    /**
     * BYRÅETS INTERNE ROM OM EN FORESPØRSEL (QA-runde 3 punkt 10, migrering 0368).
     *
     * Sant betyr at arrangøren ikke er med og aldri kan bli det. Skjermen henter da et kort
     * sammendrag av forespørselen (`GET /v1/threads/:id/booking-summary`), fordi en gjest fra
     * management ikke kan åpne byråets forespørsel. Valgfri så eldre klienter tåler svaret.
     *
     * Byråets direkte tråd til en koblet artist (0396) er intern i basen, men usann her: den har
     * ingen forespørsel å vise, og merket «Intern» ville sagt noe om en arrangør som ikke finnes.
     */
    isInternal: z.boolean().optional(),
    messageCount: z.number().int().nonnegative(),
    /** The sort key. Trigger-maintained; it moves forward every time anybody posts. */
    lastMessageAt: z.iso.datetime(),
    /**
     * Read state belongs to the READER: this is `last_message_at > last_read_at` on the caller's
     * own participant row, so the same thread is unread for one person and read for another.
     * A boolean rather than a count — see the file header for why.
     */
    hasUnread: z.boolean(),
    /** Null until the reader has opened the thread once (`-infinity` in the column). */
    lastReadAt: z.iso.datetime().nullable(),
    participantCount: z.number().int().nonnegative(),
    /**
     * WHO YOU ARE TALKING TO — the caller is **not** in this array, and **is** in
     * `participantCount`. An inbox row answers "who is this with", and naming yourself in your
     * own inbox is noise.
     *
     * Two consequences a client has to know, and the first one bit a real page:
     *
     * * `participantCount - participants.length` is NOT the number of hidden people. On an
     *   ordinary two-person thread it is 1 — and that 1 is the reader. A page computing an
     *   overflow badge that way renders "+1" on every conversation, which is what the Inbox list
     *   did until somebody looked at the screen.
     * * At most `THREAD_PARTICIPANT_PREVIEW` of the others are named, so the array is short for
     *   a genuinely large thread. `participantCount` is the whole truth about the SIZE.
     *
     * `ThreadDto` on the detail endpoint is different: it includes the caller, because that page
     * is about the thread rather than about your relationship to it.
     */
    participants: z.array(threadParticipantDtoSchema),
    /** Null only for a thread whose every message has been removed. */
    lastMessage: messagePreviewDtoSchema.nullable(),
    /**
     * FØLGER LESEREN TRÅDEN (0931)? Sant for alt i «Alle»; usant i filteret «Bookinger i
     * organisasjonen», som viser det du har tilgang til uten å følge. Valgfri fordi chatrutene
     * bruker den samme formen.
     */
    isFollowing: z.boolean().optional(),
    /** Saken tråden gjelder (0932, E2). Null for alt som ikke er en booking. */
    engagementId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'ThreadSummary' });

/** The detail view: the same thread, with every participant rather than a preview of them. */
/**
 * The open thread.
 *
 * It extends the summary schema, so read the note on `participants` there — and then read this,
 * because on this shape it says the opposite. `ThreadDto.participants` **includes the caller**
 * and is not capped at `THREAD_PARTICIPANT_PREVIEW`: the detail page is about the thread, and
 * "3 deltakere" listing only two of them would be wrong. The list endpoint answers a different
 * question — who you are talking TO — and excludes you on purpose.
 *
 * Two SQL fragments, deliberately: the list has `tpp.person_id <> p.person_id`,
 * `THREAD_PARTICIPANTS_JSON` has no such clause. `inbox.int.spec.ts` holds both halves so they
 * cannot quietly become one.
 */
export const threadDtoSchema = threadSummaryDtoSchema
  .extend({
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    createdByPersonId: z.uuid().nullable(),
    /**
     * Tråden eies av et annet hus og er delt med leseren: bookingtråden hos byrået, den direkte
     * tråden hos artisten, samtalen fra Control Room hos kunden. `can()` svarer da
     * `action_not_shared` på `thread:update` og `thread:delete`, og skjermen skjuler dem (QA6 K6, M5).
     * Valgfri fordi bare detaljruta regner den ut.
     */
    sharedWithMe: z.boolean().optional(),
    /**
     * Leseren er motparten i en tråd mellom to hus (`shared_with_organization_id`, budtråden): hen
     * legger til egne kolleger, aldri arrangørens folk (276.20 b, 1060). Eierhuset legger til som før.
     * Bare detaljruta regner den ut.
     */
    colleaguesOnly: z.boolean().optional(),
    /** Leserens egen grunn, når hen følger (0931). */
    followReason: threadFollowReasonSchema.nullable().optional(),
    /** Følgerne med grunn, fra begge hus (84, 19). */
    followers: z.array(threadFollowerDtoSchema).optional(),
    /**
     * «+ N andre med tilgang» (84). Medlemmene i husene som leser tråden gjennom konteksten (booking
     * eller artisten), og som ikke følger. Null når tråden ikke har en kontekst å lese gjennom.
     */
    othersWithAccess: z.number().int().nonnegative().nullable().optional(),
    /**
     * Husene ved navn, eierens først (19): «Nordlys ↔ Standing Ovation» når tråden går på tvers.
     * Ett navn i en tråd i ett hus.
     */
    houseNames: z.array(z.string()).optional(),
  })
  .meta({ id: 'Thread' });

/**
 * One message. `source` and `bodyFormat` are reported and never accepted — see the file header.
 * `author` is null for a system message (`messages.author_person_id IS NULL`), which is the row
 * shape a future automated post will use.
 *
 * `attachments` IS INLINE RATHER THAN A SECOND REQUEST, which is the opposite of what a task does.
 * The difference is the shape of the collection: a task page shows ONE task and can afford one more
 * call for its files, while a thread page shows twenty messages and asking per message would be
 * twenty round trips to draw one conversation (principle 6). The array is empty on nearly every
 * message and costs a few bytes when it is; the API reads them for the whole page in one query.
 */
/**
 * Meldingen et svar siterer.
 *
 * ---------------------------------------------------------------------------------------------
 * OPPLØST OG KAPPET PÅ SERVEREN, ikke en id klienten må slå opp.
 *
 * Et sitat tegnes over meldingen, og en side med tjue meldinger hvorav ti er svar ville betydd ti
 * ekstra oppslag — eller at klienten bare kunne sitere meldinger som tilfeldigvis lå i den samme
 * sida. Den siterte kan ligge hundre meldinger opp.
 *
 * KAPPET, fordi et sitat som fyller halve skjermen ikke lenger er kontekst; da er det en kopi.
 * `MESSAGE_QUOTE_LENGTH` er hvor.
 */
/** Hvor lang en emoji kan være. Nok til hudtone og ZWJ-sekvenser, for lite til en setning. */
export const MAX_REACTION_LENGTH = 32;

/**
 * Reaksjonene på én melding, gruppert per emoji (sak 81.1, Atle 3. september).
 *
 * ---------------------------------------------------------------------------------------------
 * GRUPPERT PÅ SERVEREN, MED NAVNENE MED.
 *
 * Klienten tegner «👍 3» og forteller hvem de tre er når man holder over. Å sende én rad per
 * reaksjon og gruppere i nettleseren ville flyttet en `group by` til en løkke og gjort
 * rekkefølgen tilfeldig; å sende bare et tall ville gjort «hvem» umulig å svare på, og «hvem» er
 * halve poenget — en tommel fra scenemesteren betyr noe annet enn en tommel fra en tilfeldig.
 *
 * `mine` er der fordi knappen trenger å vite om den er PÅ. Det kan utledes av navnelista, men da
 * må hver klient kjenne sin egen personId og gjøre det samme oppslaget — og en av dem kommer til
 * å gjøre det feil.
 */
export const messageReactionSchema = z
  .object({
    emoji: z.string().min(1).max(MAX_REACTION_LENGTH),
    count: z.number().int().positive(),
    /** Hvem, i navnerekkefølge. Kappet aldri: en melding har ikke tretti reaksjoner. */
    names: z.array(z.string()),
    /** Om leseren selv har reagert med denne. Se headeren. */
    mine: z.boolean(),
  })
  .meta({ id: 'MessageReaction' });
export type MessageReaction = z.infer<typeof messageReactionSchema>;

/**
 * Å sette eller fjerne én reaksjon.
 *
 * ÉN RUTE OG IKKE TO: knappen er en bryter, og «legg til hvis den ikke er der, ellers fjern» er
 * det ett trykk betyr. To ruter ville latt to raske trykk ende opp som to reaksjoner eller ingen,
 * avhengig av rekkefølgen svarene kom i.
 */
export const toggleMessageReactionRequestSchema = z
  .object({ emoji: z.string().trim().min(1).max(MAX_REACTION_LENGTH) })
  .meta({ id: 'ToggleMessageReactionRequest' });
export type ToggleMessageReactionRequest = z.infer<typeof toggleMessageReactionRequestSchema>;

export const messageQuoteSchema = z
  .object({
    id: z.uuid(),
    /** Null når forfatterens kontaktrad er myk-slettet — samme regel som `author`. */
    authorName: z.string().nullable(),
    excerpt: z.string(),
  })
  .meta({ id: 'MessageQuote' });
export type MessageQuote = z.infer<typeof messageQuoteSchema>;

/** Hvor et sitat kappes. Én linje i en samtale, ikke et avsnitt. */
export const MESSAGE_QUOTE_LENGTH = 140;

export const messageDtoSchema = z
  .object({
    id: z.uuid(),
    threadId: z.uuid(),
    author: threadParticipantDtoSchema.nullable(),
    /**
     * Om avsenderen er i DET ANDRE huset (QA M6).
     *
     * En speilet melding ligger som en kopi i hvert hus, så meldingens eget hus sier ingenting om
     * hvem som skrev den. Dette gjør det, og chatten trenger det: den samme brukeren kan være
     * medlem begge steder, og da er navnet alene ikke nok til å vite hvem som snakker.
     */
    authorIsCounterparty: z.boolean(),
    body: z.string(),
    bodyFormat: messageFormatSchema,
    /** `internal` for everything this API writes. `email` is Phase 3 and is not written here. */
    source: messageSourceSchema,
    sentAt: z.iso.datetime(),
    /** The files on this message. Empty, not absent, when there are none. */
    attachments: z.array(attachmentDtoSchema),
    /**
     * Who this message names (`PAGES.md`, Inbox [F1]). Resolved, not bare ids: the client draws
     * people and would otherwise need a lookup per mention to render one.
     *
     * Inline for the same reason `attachments` is — a thread page shows twenty messages and asking
     * per message would be twenty round trips — and it is what lets a client mark the reader's own
     * name in a body without parsing the text back into people.
     */
    mentions: z.array(threadParticipantDtoSchema),
    /**
     * Når meldingen sist ble rettet, eller null.
     *
     * ALLTID SYNLIG i klienten — «(endret)» ved siden av klokkeslettet. En samtale hvor tidligere
     * ord kan byttes ut usynlig er en samtale ingen kan sitere, og det er verre enn ikke å kunne
     * rette i det hele tatt.
     */
    editedAt: z.iso.datetime().nullable(),
    /** Meldingen dette er et svar på, oppløst — se `messageQuoteSchema`. */
    inReplyTo: messageQuoteSchema.nullable(),
    /**
     * Reaksjonene på meldingen, gruppert per emoji (sak 81.1).
     *
     * Inline av samme grunn som `attachments` og `mentions`: en tråd tegner tjue meldinger, og et
     * kall per melding ville vært tjue rundturer for å tegne én skjerm.
     */
    reactions: z.array(messageReactionSchema),
  })
  .meta({ id: 'Message' });

/**
 * The inbox query.
 *
 * `direction` is a literal rather than the usual enum: the sort column is volatile and only
 * descending is safe to page (see the file header). Fixing it in the schema means a client that
 * asks for ascending gets a 400 naming the field instead of a list that repeats rows.
 */
export const listThreadsQuerySchema = cursorQuerySchema(THREAD_SORT_KEYS)
  .extend({
    direction: z.literal('desc').default('desc'),
    /**
     * "Ulest-markering": only threads with something new since I last looked, off
     * `ix_thread_participants__unread_inbox`, which both filters and orders (migration 0010).
     */
    unread: booleanQuerySchema.optional(),
    /**
     * Threads about one object — the contact detail page's "samtaler om denne kontakten". Both
     * halves or neither; one without the other is a filter that cannot be answered.
     * Served by `ix_threads__org_linked_object`.
     *
     * There is no `closed` filter beside it YET, and the reason recorded here was wrong until
     * 31 August. It said "for a filter the plan does not ask for". `PAGES.md` asks for it by name:
     *
     *   > **Trådliste** med filtre: alle, uleste, mine, arkiverte.
     *
     * Two of those four are missing, and both are open questions rather than unwritten code — see
     * sak 59 in `docs/TIL-GODKJENNING.md`:
     *
     * * **«mine»** cannot mean anything as things stand. `message:list` is `part` for EVERY role
     *   including superadmin (decision of 28 August), so every thread anybody can list is already
     *   one they are in. The filter would be a control that never changes the list.
     * * **«arkiverte»** needs to know what archiving IS. `isClosed` is the obvious candidate and
     *   nothing sets it from the UI, so the filter would show an always-empty list until a way to
     *   archive exists — and whether an archived thread should leave the default list (the way
     *   every mail client does) is a product decision, not an implementation detail.
     *
     * The INDEX difficulty is real and is written down so the answer arrives with it: the inbox is
     * ordered off `ix_thread_participants__inbox`, per reader, and `is_closed` lives on `threads`.
     * A closed filter is therefore a test on the joined row rather than a column of the ordered
     * index — cheap while the archive is small, and a top-N sort over the whole inbox if it is not.
     * Answering it properly starts with a migration.
     */
    linkType: threadLinkTypeSchema.optional(),
    linkId: z.uuid().optional(),

    /*
     * ============================================================================================
     * DE TRE SISTE FILTRENE (KOI Del 4: «filtre Alle · Uleste · Direktemeldinger · Arkiverte;
     * søk»), og de er FILTRE PÅ DENNE LISTA framfor tre ruter.
     *
     * Ordren ber om `GET /v1/threads/search?q` som en egen rute. Huset har tatt den avgjørelsen én
     * gang før, i chattens eget søk (sak 81.4): «et filter på den samme lista framfor en egen rute
     * — se kontrakten». Grunnen holder her også og er sterkere, fordi innboksen er den tyngste
     * lista i produktet: en andre rute ville hatt sin egen paginering, sin egen markør, sin egen
     * scope-rendring og sine egne fire laterale joins — altså en andre kopi av den ene spørringen
     * som MÅ være rask, og den første til å bli uenig med den andre.
     *
     * Avviket står i `docs/KOI4-STATUS.md` og som en sak til Atle.
     *
     * `q` SØKER I EMNET OG I SISTE MELDING, ikke i hele historikken: innboksen er en liste over
     * SAMTALER, og «finn meldingen der noen nevnte curfew» er et annet spørsmål med et annet svar
     * (og en trigram-indeks bak seg). Det er skrevet ned som backlog framfor å bli halvveis gjort.
     */
    q: z.string().trim().min(1).max(200).optional(),
    /**
     * Bare direktemeldinger — tråder med en `dm_key` (migrering 0237). Ingen `dm: false`-gren:
     * «alt som ikke er en DM» er ikke et filter noen har bedt om, og et filter ingen bruker er en
     * gren ingen tester.
     */
    dm: booleanQuerySchema.optional(),
    /**
     * ARKIVET, og det er `is_closed` gjenbrukt (avgjørelse 12: «`is_closed` gjenbrukes som
     * arkiv»). Uten filteret er svaret ÅPNE tråder — en arkivert samtale skal ut av lista, slik
     * hver e-postklient gjør det — og `closed=true` er arkivfanen.
     *
     * Merk at dette endrer standardsvaret: før kom lukkede tråder med. Det er med vilje, og det er
     * avgjørelsen som ber om det.
     */
    closed: booleanQuerySchema.optional(),
    /** Bare trådene leseren har dempet (F48) — varslingsvalgene lister dem med «Slå på igjen». */
    muted: booleanQuerySchema.optional(),
    /**
     * «BOOKINGER I ORGANISASJONEN» (84): bookingtrådene du har tilgang til gjennom konteksten
     * (lesetilgang til booking eller til artisten), men ikke følger. Uten filteret er lista det du
     * følger — det er «Alle».
     */
    bookings: booleanQuerySchema.optional(),
  })
  .refine((value) => (value.linkType === undefined) === (value.linkId === undefined), {
    message: 'linkType and linkId are given together or not at all.',
    path: ['linkId'],
  })
  .meta({ id: 'ListThreadsQuery' });

export const threadListResponseSchema = page(threadSummaryDtoSchema, 'ThreadListResponse');

/**
 * Starting a thread. The first message is part of it, and that is not a convenience:
 *
 * * `last_message_at` is the inbox sort key and is only ever written by the message trigger. A
 *   thread with no messages would sit at `'-infinity'` at the very bottom of everybody's inbox —
 *   the opposite of what a brand-new thread means.
 * * A thread nobody has said anything in is not a thread. Composing one is subject, recipients
 *   and a first message, exactly as every messaging client has always modelled it.
 */
export const createThreadRequestSchema = z
  .object({
    subject: subjectSchema,
    /** The first message. Markdown; written as `source = 'internal'` like every other. */
    body: bodySchema,
    /**
     * Who else is in it. The caller is always added, whether or not they appear here, so this is
     * "the other people" — and each must be a live contact of this organization.
     */
    participantPersonIds: z.array(z.uuid()).min(1).max(MAX_THREAD_PARTICIPANTS),
    /** Omitted or null: a free-standing thread. */
    link: z.object({ type: threadLinkTypeSchema, id: z.uuid() }).nullish(),
  })
  .meta({ id: 'CreateThreadRequest' });

/**
 * Editing a thread: its subject, and whether it is closed.
 *
 * Not its participants — adding somebody is `POST /v1/threads/:id/participants`, because it is a
 * different act with a different consequence (a new person can suddenly read the history) and it
 * deserves its own route rather than being a field that quietly rewrites a set. Not its link
 * either: what a thread is about is decided when it is started.
 */
export const updateThreadRequestSchema = z
  .object({
    subject: subjectSchema.optional(),
    isClosed: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateThreadRequest' });

export const addThreadParticipantsRequestSchema = z
  .object({
    personIds: z.array(z.uuid()).min(1).max(MAX_THREAD_PARTICIPANTS),
  })
  .meta({ id: 'AddThreadParticipantsRequest' });

/**
 * Marking a thread read — the write half of "ulest-markering", and the only thing that ever
 * moves `last_read_at`.
 *
 * `upToMessageId` is what the client actually rendered. Without it the server would have to
 * guess, and both available guesses are wrong in the same way: `now()` and "the thread's current
 * last message" each mark as read a message that arrived in the milliseconds between the reader
 * looking and the request landing. With it, the reader says exactly how far they got.
 *
 * Omitted, it means "everything that exists right now" — the deliberate "mark all read" gesture,
 * where being one message ahead is what the reader asked for.
 *
 * The marker never moves backwards, whatever this says: a second tab replaying an older position
 * cannot un-read a thread.
 */
export const markThreadReadRequestSchema = z
  .object({
    upToMessageId: z.uuid().optional(),
  })
  .meta({ id: 'MarkThreadReadRequest' });

/**
 * Å arkivere eller hente fram en tråd — for LESEREN (sak 215.8). Gjelder alle kallerens seter i
 * tråden, så den som er kontakt i begge hus ikke ser den igjen i det andre.
 */
export const archiveThreadRequestSchema = z
  .object({ archived: z.boolean() })
  .meta({ id: 'ArchiveThreadRequest' });
export type ArchiveThreadRequest = z.infer<typeof archiveThreadRequestSchema>;

/**
 * DEMP TRÅDEN (F48) — for LESEREN, i alle seter, som arkivet. En dempet tråd gir ikke varsel om
 * nye meldinger; en @-nevning kommer fram likevel (0700).
 */
export const muteThreadRequestSchema = z
  .object({ muted: z.boolean() })
  .meta({ id: 'MuteThreadRequest' });
export type MuteThreadRequest = z.infer<typeof muteThreadRequestSchema>;

export const listMessagesQuerySchema = cursorQuerySchema(MESSAGE_SORT_KEYS)
  .extend({
    /** Newest first, so "load older" is the natural next page. */
    direction: sortDirectionSchema.default('desc'),
    /**
     * SØK I SAMTALEN (rettelse B1, koi4 funn 3).
     *
     * Programpostens chat har hatt dette siden sak 81.4; innboksen — den samme raden i
     * `messages`, den samme DTO-en — kunne ikke filtrere sin egen liste i det hele tatt. To
     * flater over det samme med hver sine evner er ikke to funksjoner.
     *
     * DELSTRENG OG IKKE FULLTEKST, av chattens grunn: det man leter etter i en tråd er et
     * flynummer, et romnummer eller et navn — «SK4108», «301», «Kari». Fulltekstsøk stemmer ORD
     * og finner ikke «SK41» i «SK4108», som er nøyaktig slik noen søker.
     */
    q: z.string().trim().min(1).max(200).optional(),
  })
  .meta({ id: 'ListMessagesQuery' });

export const messageListResponseSchema = page(messageDtoSchema, 'MessageListResponse');

/**
 * The most people one message may name. A message that mentions twenty is a message to the thread.
 */
export const MAX_MESSAGE_MENTIONS = 20;

/**
 * Posting a message. `source` and `bodyFormat` are absent by design — see the file header.
 *
 * `mentionPersonIds` IS THE CLIENT SAYING WHO IT MEANT, and it is the only honest way to do this.
 * The alternative — reading `@Ada Lovelace` out of the body when the notification is written —
 * fails on everything a real register contains: two people called Ada, a name with a space in it,
 * somebody renamed since, a person who types an email address. The composer picked them from a
 * list, so it knows; the service checks each one is a PARTICIPANT of the thread before writing
 * anything, which is the rule that stops a mention becoming a way to notify a stranger.
 */
export const createMessageRequestSchema = z
  .object({
    body: bodySchema,
    /** Absent and empty mean the same thing. Deduplicated by the service, not by the schema. */
    mentionPersonIds: z.array(z.uuid()).max(MAX_MESSAGE_MENTIONS).optional(),
    /**
     * Å SVARE PÅ ÉN MELDING (rettelse B1, koi4 funn 3).
     *
     * Kolonnen `in_reply_to_message_id` har ligget i `messages` siden første dag, og chatten har
     * brukt den siden sak 88. Innboksen hadde ikke feltet i kontrakten, så et svar derfra ble
     * lagret som en helt vanlig melding — sitatet forsvant i det den ble sendt.
     *
     * Et svar er ikke en undertråd: det tegnes som et sitat over meldingen, og samtalen fortsetter
     * i én strøm. Tjenesten sjekker at meldingen ligger i DENNE tråden, fordi kolonnen ikke gjør
     * det — et sitat fra en annen samtale er et sitat ingen leser kan følge.
     */
    inReplyToMessageId: z.uuid().nullish(),
    /**
     * «LEGG TIL I TRÅDEN?» (etter-KOI C2).
     *
     * En omtale får bare navngi en DELTAKER — ellers er `@` en måte å varsle en fremmed på om en
     * samtale de ikke kan åpne. Regelen er riktig, men den ble håndhevet ved å KASTE navnet:
     * «spør @Kari» ble sendt, Kari fikk ingenting, og den som skrev det trodde hun var spurt. En
     * melding som stille lar være å nå den den navngir er verre enn en som nekter.
     *
     * Med denne sann tas de navngitte inn i tråden i det meldingen sendes, og da holder regelen
     * fortsatt: de ER deltakere når omtalen skrives. Skjermen spør først — chipen «Mia Moen er
     * ikke i tråden · Legg til · Ikke nå» — og standarden er ja, fordi det er det den som skrev
     * navnet mente.
     *
     * Kravet er det samme som for `POST /v1/threads/:id/participants`: personen må være en kontakt
     * i huset. Dette er ikke en vei rundt den sjekken, bare en vei til den uten et ekstra kall.
     */
    addMentioned: z.boolean().optional(),
  })
  .meta({ id: 'CreateMessageRequest' });

export type ThreadParticipantDto = z.infer<typeof threadParticipantDtoSchema>;
export type ThreadLinkDto = z.infer<typeof threadLinkDtoSchema>;
export type MessagePreviewDto = z.infer<typeof messagePreviewDtoSchema>;
export type ThreadSummaryDto = z.infer<typeof threadSummaryDtoSchema>;
export type ThreadDto = z.infer<typeof threadDtoSchema>;
export type MessageDto = z.infer<typeof messageDtoSchema>;
/**
 * ULESTE-TELLINGENE — sidemenyens tall, i ett kall (KOI Del 4: `GET /v1/inbox/unread-counts`).
 *
 * =================================================================================================
 * TRE TALL, OG HVERT AV DEM BETYR NOE ANNET.
 *
 * `inbox` er ANTALL TRÅDER med noe nytt i, ikke antall meldinger. Det er den tellingen innboksen
 * allerede kan svare på i ett indeksoppslag (`ix_thread_participants__unread_inbox`), og det er
 * den tellingen et merke på et menypunkt betyr: «du har tre samtaler å se på».
 *
 * `tasks` er MINE ÅPNE, FORFALTE ELLER I DAG — ordrens egen definisjon. Ikke «alle mine åpne»,
 * som ville stått på 40 hele året og sluttet å bety noe.
 *
 * `threads` er per tråd, og DER er tallet meldinger: det er boblen ved siden av én samtale i
 * lista, og «3» betyr tre meldinger du ikke har sett. Bare tråder med noe nytt er med — en liste
 * over nuller er en liste over ingenting — og den er begrenset, fordi en innboks med 500 uleste
 * tråder ikke skal sende 500 rader for å tegne et merke.
 */
export const MAX_UNREAD_THREAD_COUNTS = 50;

export const threadUnreadCountSchema = z
  .object({
    threadId: z.uuid(),
    /** Meldinger sendt etter mitt lesepunkt, av andre enn meg. */
    unread: z.number().int().min(0),
  })
  .meta({ id: 'ThreadUnreadCount' });

export const inboxUnreadCountsResponseSchema = z
  .object({
    inbox: z.number().int().min(0),
    tasks: z.number().int().min(0),
    threads: z.array(threadUnreadCountSchema),
  })
  .meta({ id: 'InboxUnreadCountsResponse' });

/**
 * EN DIREKTEMELDING, ÉN PER PAR (KOI Del 4: `POST /v1/threads/dm/:personId`).
 *
 * Ruten tar personen i adressen og ingenting i kroppen: den svarer «tråden med denne personen»,
 * og lager den bare hvis den ikke finnes. Det er derfor den er `POST` og ikke `GET` — den kan
 * skrive — og derfor den er idempotent: to faner som åpner den samme samtalen samtidig får den
 * samme tråden, garantert av `ux_threads__dm_key`.
 *
 * INGEN FØRSTE MELDING, til forskjell fra `POST /v1/threads`. En DM åpnes for å skrive i den, og
 * å kreve teksten på forhånd ville betydd at «send melding til Ada» ikke kan åpne et tomt felt.
 * Prisen er at tråden står på `-infinity` til noen sier noe — se `createThreadRequestSchema` for
 * hvorfor det ellers er forbudt — og den er akseptert her fordi en tom DM er en samtale man
 * nettopp har åpnet og ikke en feil.
 */
export type ThreadUnreadCount = z.infer<typeof threadUnreadCountSchema>;
export type InboxUnreadCountsResponse = z.infer<typeof inboxUnreadCountsResponseSchema>;
export type ListThreadsQuery = z.infer<typeof listThreadsQuerySchema>;
export type ThreadListResponse = z.infer<typeof threadListResponseSchema>;
export type CreateThreadRequest = z.infer<typeof createThreadRequestSchema>;
export type UpdateThreadRequest = z.infer<typeof updateThreadRequestSchema>;
export type AddThreadParticipantsRequest = z.infer<typeof addThreadParticipantsRequestSchema>;
export type MarkThreadReadRequest = z.infer<typeof markThreadReadRequestSchema>;
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;
export type MessageListResponse = z.infer<typeof messageListResponseSchema>;
export type CreateMessageRequest = z.infer<typeof createMessageRequestSchema>;
